import { useRef } from "react"
import { motion, useMotionValue, useScroll, useTransform } from "framer-motion"
import styled from "@emotion/styled"
import hash from "@emotion/hash"

import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { headline100 } from "css/text"

import { modularTexts } from "./common"

export const txtAlign = {
  start: `a${hash("txt-start")}`,
  center: `a${hash("txt-center")}`,
  end: `a${hash("txt-end")}`,
}

export const Overlay = styled.div`
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  top: 0;
  background: var(--overlay-rgb);
  opacity: var(--opac);
  backdrop-filter: blur(calc(8px * var(--opac)));
`

export const TextContainer = styled.div`
  width: 100%;
  margin-block-end: 50vh;
  grid-column: main;

  ${getMediaQuery("m")} {
    grid-column: col 3 / span 8;
    .${txtAlign.start} & {
      grid-column: col 1 / span 7;
    }
    .${txtAlign.center} & {
      grid-column: col 3 / span 8;
    }
    .${txtAlign.end} & {
      grid-column: col 6 / span 7;
    }
  }

  ${getMediaQuery("l")} {
    grid-column: col 4 / span 6;
    .${txtAlign.start} & {
      grid-column: col 1 / span 5;
    }
    .${txtAlign.center} & {
      grid-column: col 4 / span 6;
    }
    .${txtAlign.end} & {
      grid-column: col 8 / span 5;
    }
  }

  ${modularTexts}

  &.article-overlay, 
  &.article-overlay-ref {
  &.video-overlay {
    section {
      max-width: 100%;

      display: block;
    }
  }

  &.article-overlay-ref {
    margin-block-end: 3.125rem;
  }

  &.is-mosaic-scroll {
    grid-column: col 1 / span 12;
    text-align: center;

    hgroup:first-of-type {
      margin-block-end: 3.125rem;
      display: flex;
      flex-direction: column-reverse;

      h2 {
        ${headline100}
      }

      p {
        margin-block-end: 0;
        margin-block-start: 10px;
      }
    }

    hgroup:last-child {
      margin-block-start: 3.125rem;
    }

    > p.paragraph {
      &:not(:last-child) {
        margin-block-end: 0;
      }
    }

    > p.chapo {
      margin-block-start: 20px;
    }
  }

  // A11y
  html.prefers-contrast & {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 30px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }
`

const Root = styled(motion.article)`
  ${fullGrid}
  grid-template-rows: 100vh auto;
  position: relative;

  .reference {
    grid-column: doc;
    margin-block-end: 2rem;
  }
`

export default function OverlayRoot({
  className,
  overlay,
  horizontal_align,
  titleGradient,
  chapoGradient,
  titleGradAngle,
  chapoGradAngle,
  children,
  ...props
}) {
  const console = useConsole()

  const ref = useRef()
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] })

  const opac = useTransform(scrollYProgress, [0, 0.6], [0, 1])
  const bgcol = overlay?.colors ? overlay.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`) : []
  const backgroundColor = useMotionValue(bgcol.length === 1 ? bgcol[0] : bgcol.length > 1 ? `linear-gradient(${bgcol})` : null)

  return (
    <Root
      ref={ref}
      className={`${className} ${txtAlign[horizontal_align?.align || "center"]}`}
      style={{
        "--overlay-rgb": backgroundColor,
        "--opac": opac,
        "--title-gradient": titleGradient,
        "--chapo-gradient": chapoGradient,
        "--title-grad-angle": titleGradAngle,
        "--chapo-grad-angle": chapoGradAngle,
      }}
    >
      {children}
    </Root>
  )
}
